<template>
  <div class="p-1">
    <h6 class="mb-1">
      Select Products to Ship
    </h6>
    <p class="text-muted mb-2">
      Products across all confirmed orders in "{{ collectionName }}" that are ready to ship.
    </p>

    <div
      v-if="isLoading"
      class="text-center py-3"
    >
      <b-spinner />
    </div>

    <template v-else>
      <div
        v-if="products.length === 0"
        class="text-center py-3"
      >
        <p class="text-muted">
          No eligible products found for this collection.
        </p>
      </div>

      <template v-else>
        <div class="mb-1">
          <b-form-input
            v-model="searchQuery"
            placeholder="Search by product name or style codes (comma-separated)..."
            class="mb-1"
          />
        </div>

        <div class="mb-1 d-flex justify-content-between align-items-center">
          <b-form-checkbox
            :checked="allFilteredSelected"
            :indeterminate="someFilteredSelected && !allFilteredSelected"
            @change="toggleAll"
          >
            Select All ({{ filteredProducts.length }})
          </b-form-checkbox>
          <small class="text-muted">{{ localSelectedIds.length }} selected</small>
        </div>

        <div
          class="product-list scroll-bar"
          style="max-height: 400px; overflow-y: auto;"
        >
          <b-card
            v-for="product in filteredProducts"
            :key="product.productId"
            no-body
            class="mb-50 p-75 cursor-pointer"
            :class="{ 'border-primary': localSelectedIds.includes(product.productId) }"
            @click="toggleProduct(product.productId)"
          >
            <div class="d-flex align-items-center">
              <b-form-checkbox
                :checked="localSelectedIds.includes(product.productId)"
                class="mr-75"
                @change="toggleProduct(product.productId)"
                @click.native.stop
              />
              <b-img-lazy
                class="rounded mr-1"
                :src="product.image || defaultImage"
                :alt="product.name"
                width="48"
                height="48"
                style="object-fit: cover;"
                @error.native="imageLoadError"
              />
              <div class="flex-grow-1">
                <div class="font-weight-bold">
                  {{ product.name }}
                </div>
                <small
                  v-if="product.styleCode"
                  class="text-muted"
                >
                  {{ product.styleCode }}
                </small>
              </div>
              <b-badge
                pill
                :style="{ background: getStatusColor(product.status) }"
                class="text-white"
              >
                {{ getStatusText(product.status) }}
              </b-badge>
            </div>
          </b-card>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import {
  BSpinner,
  BFormCheckbox,
  BCard,
  BBadge,
  BImgLazy,
  BFormInput,
} from 'bootstrap-vue'
import store from '@/store'
import { imageLoadError, getDefaultProductImage } from '@/@core/utils/utils'
import { getStatusTextAndColorCode } from '@/common-utils'
import { GET_BULK_SHIP_PRODUCTS } from '@/store/modules/order.module'

export default {
  components: {
    BSpinner,
    BFormCheckbox,
    BCard,
    BBadge,
    BImgLazy,
    BFormInput,
  },
  props: {
    collectionId: {
      type: String,
      required: true,
    },
    collectionName: {
      type: String,
      required: true,
    },
    selectedProductIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      products: [],
      localSelectedIds: [...this.selectedProductIds],
      imageLoadError,
      defaultImage: getDefaultProductImage(),
      searchQuery: '',
    }
  },
  computed: {
    filteredProducts() {
      if (!this.searchQuery) return this.products
      const terms = this.searchQuery.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
      if (terms.length === 0) return this.products
      return this.products.filter(p => terms.some(q => (p.name || '').toLowerCase().includes(q)
          || (p.styleCode || '').toLowerCase().includes(q)))
    },
    allFilteredSelected() {
      return this.filteredProducts.length > 0
        && this.filteredProducts.every(p => this.localSelectedIds.includes(p.productId))
    },
    someFilteredSelected() {
      return this.filteredProducts.some(p => this.localSelectedIds.includes(p.productId))
    },
  },
  watch: {
    collectionId: {
      immediate: true,
      handler() {
        this.loadProducts()
      },
    },
  },
  methods: {
    async loadProducts() {
      if (!this.collectionId) return
      this.isLoading = true

      try {
        const res = await store.dispatch(GET_BULK_SHIP_PRODUCTS, {
          collectionId: this.collectionId,
        })
        this.products = res?.data?.data?.products || []
      } catch {
        this.products = []
      } finally {
        this.isLoading = false
      }
    },
    getStatusText(status) {
      return getStatusTextAndColorCode(status).text || status
    },
    getStatusColor(status) {
      return getStatusTextAndColorCode(status).colorCode || '#28c76f'
    },
    toggleProduct(productId) {
      const idx = this.localSelectedIds.indexOf(productId)
      if (idx >= 0) {
        this.localSelectedIds.splice(idx, 1)
      } else {
        this.localSelectedIds.push(productId)
      }
      this.$emit('update', [...this.localSelectedIds])
    },
    toggleAll(checked) {
      if (checked) {
        const filteredIds = this.filteredProducts.map(p => p.productId)
        const merged = new Set([...this.localSelectedIds, ...filteredIds])
        this.localSelectedIds = [...merged]
      } else {
        const filteredIds = new Set(this.filteredProducts.map(p => p.productId))
        this.localSelectedIds = this.localSelectedIds.filter(id => !filteredIds.has(id))
      }
      this.$emit('update', [...this.localSelectedIds])
    },
  },
}
</script>
